import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Film, RefreshCw, Play, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import api, { apiError, fmtTimeParis } from '../../lib/api';
import { srcMedia, resumeSondage, dureeTexte } from '../../lib/canalPosts';
import {
  JOURS_SEMAINE, ICONES, cleJourParis, moisParis, decalerMois, titreMois, prefixeMois, grilleMois, periodeGrille,
  elementsCalendrier, filtrerElements, compterParJour, totauxMois, texteTotaux, texteJour, libelleJour, legendeCourte, miniaturesJour,
} from '../../lib/canalCalendrier';

// 17/09 (Marlon) : « Vue calendrier » de la page Canal — combien de stories 📸, posts 📢, sondages 📊 et vocaux 🎤 sont
// prévus chaque jour du mois, filtre par modèle et par type, détail du jour au clic. Heure de Paris.
// Ordinateur : jusqu'à 3 mini-vignettes par case puis « +N ». Téléphone : compteurs seulement (vignettes dans le détail).
// ⚠ index.css habille tout bouton dont la classe contient « bg-indigo-500 » (dégradé) et tout bloc « bg-white/[0.02] » :
// les cases du calendrier utilisent donc bg-indigo-400/15 et bg-white/[0.03].
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUTS = {
  story: { pending: 'Programmée', sending: 'Envoi…', posted: 'Postée', failed: 'Échec' },
  post: { pending: 'Programmé', sending: 'Envoi…', posted: 'Publié', failed: 'Échec' },
};
const COULEURS = {
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  sending: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  posted: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  failed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
};
const TYPES = [['tout', 'Tout'], ['story', '📸 Stories'], ['post', '📢 Posts'], ['sondage', '📊 Sondages'], ['vocal', '🎤 Vocaux']];
const LIBELLES = { story: 'Story', post: 'Post', sondage: 'Sondage', vocal: 'Vocal' };
const COULEUR_COMPTEUR = { story: 'text-sky-200', post: 'text-fuchsia-200', sondage: 'text-amber-200', vocal: 'text-emerald-200' };

// Mini-vignette d'une case (ordinateur) : image légère en lazy, petit ▶ pour une vidéo ; sinon l'icône du type.
function Mini({ x }) {
  const [casse, setCasse] = useState(false);
  const src = srcMedia(x.vignette, BACKEND);
  return (
    <span data-testid={`cal-mini-el-${x.id}`} title={`${fmtTimeParis(x.quand)} · ${LIBELLES[x.type]}`}
      className="relative w-5 h-5 shrink-0 rounded overflow-hidden bg-black/40 border border-white/10 flex items-center justify-center text-[10px] leading-none">
      {src && !casse ? <img src={src} alt="" loading="lazy" onError={() => setCasse(true)} className="w-full h-full object-cover" /> : ICONES[x.type]}
      {x.video && <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white"><Play size={8} fill="currentColor" /></span>}
    </span>
  );
}

// Vignette du détail du jour (plus grande) : clic → aperçu grand format. Sondage / vocal / sans image = icône.
function VignetteJour({ x, onOuvrir }) {
  const [casse, setCasse] = useState(false);
  const leger = srcMedia(x.vignette, BACKEND);
  const photo = x.video ? '' : srcMedia(x.media, BACKEND);
  const image = leger || photo;
  if (x.type === 'sondage' || x.type === 'vocal' || !image || casse) {
    const ouvrable = (x.type === 'story' || x.type === 'post') && srcMedia(x.media, BACKEND);
    const tuile = (
      <span className="w-14 h-14 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center text-slate-300 text-lg">
        {x.video ? <Film size={16} /> : (x.type === 'story' || x.type === 'post' ? '🖼️' : ICONES[x.type])}
      </span>
    );
    return ouvrable
      ? <button type="button" onClick={onOuvrir} data-testid={`cal-icone-${x.id}`} className="shrink-0 hover:opacity-80" title="Voir">{tuile}</button>
      : <span data-testid={`cal-icone-${x.id}`} className="shrink-0">{tuile}</span>;
  }
  return (
    <button type="button" onClick={onOuvrir} data-testid={`cal-ouvrir-${x.id}`} title={x.video ? 'Voir la vidéo' : 'Voir la photo'}
      className="relative w-14 h-14 rounded-lg overflow-hidden bg-black/40 border border-white/10 shrink-0 hover:border-indigo-400/60">
      <img src={image} alt="" loading="lazy" onError={() => setCasse(true)} data-testid={`cal-vignette-${x.id}`} className="w-full h-full object-cover" />
      {x.video && <span className="absolute inset-0 flex items-center justify-center bg-black/25 text-white"><Play size={16} fill="currentColor" /></span>}
    </button>
  );
}

// Aperçu grand format : fichier d'origine s'il est encore sur le serveur (vidéo : commandes, jamais de lecture auto), sinon la vignette.
function Apercu({ x, onFermer }) {
  const [origineCasse, setOrigineCasse] = useState(false);
  useEffect(() => { setOrigineCasse(false); }, [x]);
  const origine = x ? srcMedia(x.media, BACKEND) : '';
  const leger = x ? srcMedia(x.vignette, BACKEND) : '';
  return (
    <Dialog open={!!x} onOpenChange={(o) => { if (!o) onFermer(); }}>
      <DialogContent aria-describedby={undefined} data-testid="cal-apercu" className="max-w-3xl bg-[#111015] border-white/10 p-4 text-white">
        <DialogTitle className="text-sm font-semibold pr-6 truncate">{x ? `${fmtTimeParis(x.quand)} · ${LIBELLES[x.type]}${x.legende ? ` — ${legendeCourte(x.legende, 60)}` : ''}` : ''}</DialogTitle>
        {x && origine && !origineCasse && (x.video
          ? <video key={origine} src={origine} controls playsInline preload="metadata" onError={() => setOrigineCasse(true)} data-testid="cal-apercu-video" className="w-full max-h-[70vh] rounded-lg bg-black" />
          : <img src={origine} alt="" onError={() => setOrigineCasse(true)} data-testid="cal-apercu-photo" className="w-full max-h-[70vh] object-contain rounded-lg bg-black/40" />)}
        {x && (!origine || origineCasse) && leger && (
          <div className="space-y-2">
            <img src={leger} alt="" data-testid="cal-apercu-vignette" className="w-full max-h-[60vh] object-contain rounded-lg bg-black/40" />
            <div className="text-[11px] text-slate-500 text-center">Aperçu réduit : le fichier d'origine n'est plus sur le serveur.</div>
          </div>
        )}
        {x && (!origine || origineCasse) && !leger && <div className="py-10 text-center text-sm text-slate-400">Le fichier n'est plus disponible sur le serveur.</div>}
      </DialogContent>
    </Dialog>
  );
}

export default function CanalCalendrier() {
  const [mois, setMois] = useState(() => moisParis());
  const [jourChoisi, setJourChoisi] = useState(() => cleJourParis(new Date()));
  const [stories, setStories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [bots, setBots] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [modele, setModele] = useState('');
  const [type, setType] = useState('tout');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [apercu, setApercu] = useState(null);
  const requete = useRef(0);
  const detailRef = useRef(null);

  const jours = useMemo(() => grilleMois(mois), [mois]);
  const periode = useMemo(() => periodeGrille(jours), [jours]);

  useEffect(() => {
    api.get('/canal-modeles').then(({ data }) => setModeles(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // Stories + posts de toute la grille (tous modèles) ; le filtre se fait ici, sans rappeler le serveur.
  const charger = useCallback((silencieux) => {
    const n = requete.current + 1;
    requete.current = n;
    if (!silencieux) setChargement(true);
    const params = { debut: periode.debut, fin: periode.fin };
    return Promise.all([
      api.get('/stories', { params }),
      api.get('/canal-posts', { params }),
      api.get('/canal-bots').catch(() => null),
    ]).then(([s, p, b]) => {
      if (requete.current !== n) return;   // réponse d'un autre mois arrivée en retard
      setStories(Array.isArray(s.data) ? s.data : []);
      setPosts(Array.isArray(p.data) ? p.data : []);
      if (b && Array.isArray(b.data)) setBots(b.data);
      setErreur('');
    }).catch((e) => {
      if (requete.current !== n) return;
      if (!silencieux) setErreur(apiError(e, 'Impossible de charger le calendrier'));
    }).finally(() => {
      if (requete.current === n) setChargement(false);
    });
  }, [periode]);
  useEffect(() => { charger(false); }, [charger]);
  useEffect(() => { const t = setInterval(() => charger(true), 60000); return () => clearInterval(t); }, [charger]);

  const elements = useMemo(() => filtrerElements(elementsCalendrier(stories, posts, bots), { modele, type }), [stories, posts, bots, modele, type]);
  const parJour = useMemo(() => compterParJour(elements), [elements]);
  const listeParJour = useMemo(() => {
    const m = {};
    elements.forEach(x => { (m[x.cle] = m[x.cle] || []).push(x); });
    return m;
  }, [elements]);
  const totaux = useMemo(() => totauxMois(elements, mois), [elements, mois]);
  const duJour = listeParJour[jourChoisi] || [];
  const aujourdhui = cleJourParis(new Date());
  const nomModele = (id) => (modeles.find(m => m.id === id) || {}).name;

  const changerMois = (n) => {
    const m = decalerMois(mois, n);
    setMois(m);
    setJourChoisi(aujourdhui.startsWith(prefixeMois(m)) ? aujourdhui : '');
  };
  const allerAujourdhui = () => { setMois(moisParis()); setJourChoisi(aujourdhui); };
  const choisirJour = (cle) => {
    setJourChoisi(cle);
    const partiMois = cle.slice(0, 8) !== prefixeMois(mois);
    if (partiMois) setMois({ annee: Number(cle.slice(0, 4)), mois: Number(cle.slice(5, 7)) });
    // Téléphone / petit écran : le détail est sous la grille → on le fait apparaître.
    setTimeout(() => {
      const e = detailRef.current;
      if (e && e.getBoundingClientRect().top > window.innerHeight - 120 && e.scrollIntoView) e.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const cJour = parJour[jourChoisi] || { story: 0, post: 0, sondage: 0, vocal: 0, echecs: 0 };

  return (
    <div className="space-y-4" data-testid="canal-calendrier">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => changerMois(-1)} data-testid="cal-precedent" aria-label="Mois précédent" title="Mois précédent"
              className="w-8 h-8 rounded-md border border-white/10 bg-white/5 text-slate-300 hover:text-white hover:bg-white/10 flex items-center justify-center"><ChevronLeft size={16} /></button>
            <div data-testid="cal-titre" className="min-w-[138px] text-center text-[15px] font-semibold text-white first-letter:uppercase">{titreMois(mois)}</div>
            <button type="button" onClick={() => changerMois(1)} data-testid="cal-suivant" aria-label="Mois suivant" title="Mois suivant"
              className="w-8 h-8 rounded-md border border-white/10 bg-white/5 text-slate-300 hover:text-white hover:bg-white/10 flex items-center justify-center"><ChevronRight size={16} /></button>
            <button type="button" onClick={allerAujourdhui} data-testid="cal-aujourdhui"
              className="ml-1 px-3 h-8 rounded-md border border-white/10 bg-white/5 text-xs text-slate-200 hover:bg-white/10">Aujourd'hui</button>
            {chargement && <Loader2 size={14} className="ml-1 animate-spin text-slate-500" data-testid="cal-chargement" />}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={modele} onChange={e => setModele(e.target.value)} data-testid="cal-filtre-modele" aria-label="Modèle"
              className="h-8 bg-[#18171C] border border-white/10 rounded-lg px-2 text-[13px] text-white outline-none">
              <option value="">Tous les modèles</option>
              {modeles.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <div className="flex flex-wrap gap-1 p-1 rounded-lg bg-white/5 border border-white/10">
              {TYPES.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setType(v)} data-testid={`cal-type-${v}`} aria-pressed={type === v}
                  className={`px-2.5 h-6 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${type === v ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div data-testid="cal-totaux" className="text-sm font-medium text-white">{texteTotaux(totaux, type)}</div>
          {(totaux.publies > 0 || totaux.echecs > 0) && (
            <div data-testid="cal-totaux-detail" className="text-[11px] text-slate-500">
              {totaux.publies > 0 && <span>dont {totaux.publies} déjà {type === 'story' ? (totaux.publies > 1 ? 'postées' : 'postée') : (totaux.publies > 1 ? 'publiés' : 'publié')}</span>}
              {totaux.publies > 0 && totaux.echecs > 0 && ' · '}
              {totaux.echecs > 0 && <span className="text-rose-300/80">{totaux.echecs} en échec</span>}
            </div>
          )}
        </div>
        {erreur && (
          <div data-testid="cal-erreur" className="flex flex-wrap items-center gap-2 text-[12px] text-rose-300">
            {erreur}
            <button type="button" onClick={() => charger(false)} className="underline hover:text-white flex items-center gap-1"><RefreshCw size={11} /> Réessayer</button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-1.5 sm:p-3">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {JOURS_SEMAINE.map(j => <div key={j} className="text-center text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-500">{j}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="cal-grille">
          {jours.map(j => {
            const c = parJour[j.cle];
            const choisi = j.cle === jourChoisi;
            const estAujourdhui = j.cle === aujourdhui;
            const { montres, reste } = miniaturesJour(listeParJour[j.cle]);
            return (
              <button key={j.cle} type="button" onClick={() => choisirJour(j.cle)} data-testid={`cal-jour-${j.cle}`}
                data-stories={c ? c.story : 0} data-posts={c ? c.post : 0} data-sondages={c ? c.sondage : 0} data-vocaux={c ? c.vocal : 0}
                data-echecs={c ? c.echecs : 0} data-mois={j.dansMois ? '1' : '0'}
                title={`${libelleJour(j.cle)} : ${c ? texteJour(c) : 'rien de prévu'}`}
                className={`min-w-0 min-h-[56px] sm:min-h-[80px] lg:min-h-[96px] rounded-lg border p-1 sm:p-1.5 flex flex-col items-start gap-0.5 text-left overflow-hidden transition-colors
                  ${choisi ? 'border-indigo-400/70 bg-indigo-400/15' : 'border-white/5 bg-white/[0.03] hover:border-white/20'} ${j.dansMois ? '' : 'opacity-40'}`}>
                <div className="w-full flex items-center justify-between gap-0.5">
                  <span className={`text-[11px] sm:text-xs leading-none tabular-nums ${estAujourdhui ? 'bg-indigo-500 text-white rounded-full w-5 h-5 flex items-center justify-center font-semibold' : 'text-slate-300'}`}>{j.jour}</span>
                  {c && c.echecs > 0 && <span data-testid={`cal-echecs-${j.cle}`} className="text-[10px] leading-none text-rose-300/80">⚠{c.echecs}</span>}
                </div>
                {['story', 'post', 'sondage', 'vocal'].map(k => (c && c[k] > 0
                  ? <span key={k} className={`text-[10px] sm:text-[12px] leading-tight whitespace-nowrap tabular-nums ${COULEUR_COMPTEUR[k]}`}>{ICONES[k]}{' '}{c[k]}</span>
                  : null))}
                {montres.length > 0 && (
                  <span data-testid={`cal-mini-${j.cle}`} className="hidden lg:flex items-center gap-0.5 mt-auto pt-0.5">
                    {montres.map(x => <Mini key={x.id} x={x} />)}
                    {reste > 0 && <span data-testid={`cal-plus-${j.cle}`} className="text-[10px] text-slate-400 leading-none pl-0.5">+{reste}</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-2 px-1 text-[10px] sm:text-[11px] text-slate-500">📸 stories · 📢 posts · 📊 sondages · 🎤 vocaux · ⚠ échecs — heure de Paris, les annulés ne comptent pas</div>
      </div>

      <div ref={detailRef} data-testid="cal-detail" className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden scroll-mt-4">
        {!jourChoisi ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">Clique sur un jour pour voir ce qui est prévu.</div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-white/5 flex flex-wrap items-baseline justify-between gap-x-3">
              <div data-testid="cal-detail-titre" className="text-sm font-semibold text-white first-letter:uppercase">{libelleJour(jourChoisi)}</div>
              <div data-testid="cal-detail-compte" className="text-[11px] text-slate-400">{texteJour(cJour)}</div>
            </div>
            {duJour.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">Rien de prévu ce jour-là{modele || type !== 'tout' ? ' (avec ces filtres)' : ''}.</div>}
            <div className="divide-y divide-white/5">
              {duJour.map(x => {
                const s = x.sondage || {};
                return (
                  <div key={x.id} data-testid={`cal-element-${x.id}`} data-type={x.type} data-statut={x.status} className="flex items-start gap-3 px-4 py-2.5">
                    <VignetteJour x={x} onOuvrir={() => setApercu(x)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px]">
                        <span className="text-white font-semibold tabular-nums">{fmtTimeParis(x.quand)}</span>
                        <span className="text-slate-300">{ICONES[x.type]} {LIBELLES[x.type]}</span>
                        <span className={x.modele && nomModele(x.modele) ? 'text-indigo-200' : 'text-slate-500'}>{(x.modele && nomModele(x.modele)) || 'sans modèle'}</span>
                      </div>
                      {x.type === 'sondage' ? (
                        <div data-testid={`cal-sondage-${x.id}`}>
                          <div className="text-[13px] text-white break-words">{s.question || 'Sondage'}</div>
                          <ul className="mt-0.5 space-y-0.5">
                            {(s.options || []).map((o, i) => {
                              const bonne = s.quiz && (s.bonnes || []).includes(i);
                              return (
                                <li key={i} className={`text-[12px] flex items-center gap-1.5 ${bonne ? 'text-emerald-300' : 'text-slate-300'}`}>
                                  {bonne ? <Check size={11} className="shrink-0" /> : <span className={`w-2.5 h-2.5 shrink-0 border border-slate-500 ${s.multiple ? 'rounded-sm' : 'rounded-full'}`} />}
                                  <span className="break-words min-w-0">{o}</span>
                                </li>
                              );
                            })}
                          </ul>
                          <div className="text-[11px] text-slate-500">{resumeSondage(s)}</div>
                        </div>
                      ) : (
                        <div className="text-[13px] text-white truncate">{legendeCourte(x.legende) || <span className="text-slate-500 italic">sans légende</span>}</div>
                      )}
                      {x.type === 'vocal' && (
                        <div className="mt-0.5 space-y-1" data-testid={`cal-vocal-${x.id}`}>
                          <div className="text-[11px] text-slate-400">🎤 {x.duree ? dureeTexte(x.duree) : 'durée inconnue'}{x.media ? '' : ' · fichier plus sur le serveur'}</div>
                          {x.media && <audio src={srcMedia(x.media, BACKEND)} controls preload="none" data-testid={`cal-audio-${x.id}`} className="w-full max-w-[280px] h-8" />}
                        </div>
                      )}
                      <div className="text-[11px] text-slate-500 truncate">{x.cible}{x.video ? ' · vidéo' : ''}</div>
                      {x.status === 'failed' && x.erreur && <div className="text-[11px] text-rose-400/90 truncate">{x.erreur}</div>}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${COULEURS[x.status] || COULEURS.pending}`}>{(STATUTS[x.type === 'story' ? 'story' : 'post'] || {})[x.status] || x.status}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      <Apercu x={apercu} onFermer={() => setApercu(null)} />
    </div>
  );
}
